import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import type { ApiClient } from '../api.js';

interface Props {
  api: ApiClient;
}

/**
 * Application-owned TOTP enrollment. Starts enrollment (POST /api/mfa/enroll/start)
 * to get an otpauth URI + Base32 secret, renders the QR entirely client-side via
 * `qrcode`, then confirms a single code (POST /api/mfa/enroll/confirm). On success
 * the user is enrolled and already inside the step-up grace window, so we return
 * to the dashboard.
 */
export function MfaSetup({ api }: Props): JSX.Element {
  const navigate = useNavigate();
  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUri, setQrDataUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .mfaEnrollStart()
      .then(async ({ otpauthUri, secret }) => {
        if (cancelled) return;
        setSecret(secret);
        const dataUri = await QRCode.toDataURL(otpauthUri);
        if (!cancelled) setQrDataUri(dataUri);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const handleVerify = async (): Promise<void> => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      await api.mfaEnrollConfirm(code);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setVerifying(false);
    }
  };

  if (error && !secret) {
    return (
      <div className="center">
        <h2>Setup Error</h2>
        <p className="error">{error}</p>
        <button onClick={() => navigate('/')}>Back to dashboard</button>
      </div>
    );
  }

  if (!secret) {
    return <div className="center">Setting up second factor…</div>;
  }

  return (
    <div className="center mfa-setup">
      <h2>Set Up Your Second Factor</h2>
      <p>
        Scan this QR code with your authenticator app (Google Authenticator,
        1Password, Authy, etc.)
      </p>

      {qrDataUri && (
        <div className="qr-code">
          <img src={qrDataUri} alt="TOTP QR Code" width={200} height={200} />
        </div>
      )}

      <p className="muted">Or enter this code manually:</p>
      <code className="secret-code">{secret}</code>

      <hr />

      <p>Enter the 6-digit code from your authenticator app:</p>

      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        placeholder="000000"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        className="code-input"
      />

      {error && <p className="error">{error}</p>}

      <button
        className="primary"
        onClick={() => void handleVerify()}
        disabled={code.length !== 6 || verifying}
      >
        {verifying ? 'Verifying…' : 'Verify and Enable'}
      </button>

      <button className="secondary" onClick={() => navigate('/')}>
        Cancel
      </button>
    </div>
  );
}
